import { Timestamp } from 'firebase/firestore';
import { VTODocument, Quarter } from '../types';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Utility functions for VTO module
 * Migrated from source components
 */

/**
 * Get the current quarter based on the current month
 * Migrated from VTOSelector.tsx
 */
export function getCurrentQuarter(): Quarter {
  const month = new Date().getMonth() + 1;
  if (month <= 3) return 'Q1';
  if (month <= 6) return 'Q2';
  if (month <= 9) return 'Q3';
  return 'Q4';
}

/**
 * Get the current year
 * Migrated from VTOSelector.tsx
 */
export function getCurrentYear(): number {
  return new Date().getFullYear();
}

/**
 * Get the quarter end date as a string
 * Migrated from CreateRocksFromVTOModal.tsx
 */
export function getQuarterEndDate(quarter: Quarter, year: number): string {
  const quarterEndMonths: Record<Quarter, number> = {
    Q1: 2,  // March (0-indexed: 2)
    Q2: 5,  // June
    Q3: 8,  // September
    Q4: 11, // December
  };

  const month = quarterEndMonths[quarter];
  const lastDay = new Date(year, month + 1, 0).getDate();
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

/**
 * Parse a financial value string (e.g., "$50M", "$8M") to a number
 * Migrated from ThreeYearProgress.tsx
 */
export function parseFinancialValue(value: string | null | undefined): number {
  if (!value) return 0;
  const cleaned = value.replace(/[^0-9.]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Format a number as currency string (e.g., 5000000 -> "$5.0M")
 * Migrated from ThreeYearProgress.tsx
 */
export function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

/**
 * Calculate months elapsed since a creation date
 * Migrated from ThreeYearProgress.tsx
 */
export function calculateMonthsElapsed(createdAt: Timestamp | Date | string): number {
  const created = createdAt instanceof Date 
    ? createdAt 
    : typeof createdAt === 'string' 
    ? new Date(createdAt)
    : createdAt.toDate();
  const now = new Date();
  const months = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
  return Math.max(0, months);
}

/**
 * Parse a target input that may use shorthand into a raw number.
 * Accepts: "$50M", "50M", "500K", "50000000", "$13,000,000", "1.5B".
 * Returns 0 for empty/invalid input.
 */
export function parseTargetInput(raw: string | number | null | undefined): number {
  if (raw === null || raw === undefined || raw === '') return 0;
  if (typeof raw === 'number') return isNaN(raw) ? 0 : raw;

  const cleaned = raw.trim().replace(/[$,\s]/g, '');
  if (!cleaned) return 0;

  const match = cleaned.match(/^(-?\d*\.?\d+)([kKmMbB]?)$/);
  if (!match) {
    const fallback = parseFloat(cleaned);
    return isNaN(fallback) ? 0 : fallback;
  }

  const value = parseFloat(match[1]);
  if (isNaN(value)) return 0;

  const suffix = match[2].toLowerCase();
  const multiplier = suffix === 'b' ? 1_000_000_000 : suffix === 'm' ? 1_000_000 : suffix === 'k' ? 1_000 : 1;
  return value * multiplier;
}

/**
 * Format a raw number as target-display shorthand (e.g., 50000000 -> "50M", 500000 -> "500K").
 * Does NOT include a currency symbol; callers prepend "$" where appropriate.
 * Returns '' for 0/empty so inputs can show their placeholder and read-only views can show "Not set".
 */
export function fmtTargetDisplay(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value) || value === 0) return '';
  const trim = (n: number) => parseFloat(n.toFixed(2)).toString();
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${trim(value / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${trim(value / 1_000_000)}M`;
  if (abs >= 1_000) return `${trim(value / 1_000)}K`;
  return `${trim(value)}`;
}

/**
 * Time-progress for the dashboard: months elapsed since the horizon start
 * (target date minus `totalMonths`) up to now, clamped to [0, totalMonths].
 * Falls back to `fallbackStart` (e.g., the V/TO createdAt) if the target date can't be parsed.
 */
export function calculateHorizonProgress(
  targetDateStr: string | null | undefined,
  totalMonths: number,
  fallbackStart?: Timestamp | Date | string
): { elapsed: number; total: number } {
  const now = new Date();
  let start: Date | null = null;

  if (targetDateStr) {
    const target = new Date(targetDateStr);
    if (!isNaN(target.getTime())) {
      start = new Date(target);
      start.setMonth(start.getMonth() - totalMonths);
    }
  }

  if (!start && fallbackStart) {
    start = fallbackStart instanceof Date
      ? fallbackStart
      : typeof fallbackStart === 'string'
      ? new Date(fallbackStart)
      : fallbackStart.toDate();
  }

  if (!start || isNaN(start.getTime())) {
    return { elapsed: 0, total: totalMonths };
  }

  const elapsed = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  return { elapsed: Math.max(0, Math.min(elapsed, totalMonths)), total: totalMonths };
}



/**
 * Export VTO to PDF
 * Captures the provided Vision and Traction elements and generates a PDF
 */
export async function exportVTOToPDF(
  vto: VTODocument, 
  visionElement: HTMLElement | null, 
  tractionElement: HTMLElement | null
) {
  if (!visionElement || !tractionElement) {
    console.error('Vision or Traction elements not found');
    return;
  }

  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Capture Vision View
    const visionCanvas = await html2canvas(visionElement, {
      scale: 4, // Improve quality further
      useCORS: true,
      logging: false,
      windowWidth: 1200, // Force specific width for consistent rendering
      onclone: (element) => {
        const style = element.createElement('style');
        style.innerHTML = `
          * {
            letter-spacing: normal !important;
            font-variant-ligatures: none !important;
            -webkit-font-smoothing: antialiased !important;
            -moz-osx-font-smoothing: grayscale !important;
            font-family: Arial, Helvetica, sans-serif !important;
          }
        `;
        element.head.appendChild(style);
      }
    });

    const visionImgData = visionCanvas.toDataURL('image/png');
    const visionImgProps = pdf.getImageProperties(visionImgData);
    const visionRatio = visionImgProps.width / visionImgProps.height;
    const visionHeight = pdfWidth / visionRatio;

    pdf.addImage(visionImgData, 'PNG', 0, 0, pdfWidth, visionHeight);

    // Add new page for Traction View
    pdf.addPage();

    // Capture Traction View
    const tractionCanvas = await html2canvas(tractionElement, {
      scale: 4, // Improve quality further
      useCORS: true,
      logging: false,
      windowWidth: 1200, // Force specific width for consistent rendering
      onclone: (element) => {
        const style = element.createElement('style');
        style.innerHTML = `
          * {
            letter-spacing: normal !important;
            font-variant-ligatures: none !important;
            -webkit-font-smoothing: antialiased !important;
            -moz-osx-font-smoothing: grayscale !important;
            font-family: Arial, Helvetica, sans-serif !important;
          }
        `;
        element.head.appendChild(style);
      }
    });

    const tractionImgData = tractionCanvas.toDataURL('image/png');
    const tractionImgProps = pdf.getImageProperties(tractionImgData);
    const tractionRatio = tractionImgProps.width / tractionImgProps.height;
    const tractionHeight = pdfWidth / tractionRatio;

    pdf.addImage(tractionImgData, 'PNG', 0, 0, pdfWidth, tractionHeight);

    // Save PDF
    pdf.save(`VTO-${vto.quarter}-${vto.year}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Failed to generate PDF. Please try again.');
  }
}

